import type { ReactNode } from "react";
import { TopBar } from "@/components/TopBar";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { FaqSection } from "@/components/FaqSection";
import type { FaqItem } from "@/components/FaqSection";
import { useLanguage } from "@/lib/language";
import { content, whatsappUrl, type Lang } from "@/lib/content";
import {
  ArticleSidebar,
  EditorialImage,
  type ArticleImage,
  type ArticleSectionLink,
} from "@/components/insights/ArticleEditorial";
import { LangLink } from "@/components/LangLink";

export interface InsightMeta {
  slug: string;
  metaTitle: Record<Lang, string>;
  title: Record<Lang, string>;
  description: Record<Lang, string>;
  datePublished: string; // ISO date, for Article schema
  dateModified?: string; // Actual substantive update, preserved separately from publication
  enquiryMessage?: Record<Lang, string>;
  enquiryTitle?: Record<Lang, string>;
  enquiryButton?: Record<Lang, string>;
  dateLabel: Record<Lang, string>; // visible "Updated July 2026"
  faq: readonly FaqItem[];
  image?: ArticleImage;
  keyPoints?: Record<Lang, string>[];
  sections?: ArticleSectionLink[];
}

const eyebrowLabel = { en: "Insight", ar: "دليل" };
const updatedLabel = { en: "Updated", ar: "آخر تحديث" };

/** Shared premium layout for insight/guide articles. Renders header, the
 * article body (children), the FAQ, and a closing CTA. */
export function InsightShell({ meta, children }: { meta: InsightMeta; children: ReactNode }) {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-background">
      <TopBar />
      <Navbar />
      <main>
        {/* HEADER */}
        <header className="border-b border-border bg-[#f5f3ef]">
          <div
            className={`mx-auto max-w-[1280px] px-5 lg:px-8 py-14 sm:py-20 text-start ${meta.image ? "grid items-center gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(320px,440px)] lg:gap-16" : ""}`}
          >
            <div className="min-w-0 max-w-[48rem]">
              <LangLink
                to="/insights"
                className="mb-6 inline-flex min-h-11 items-center text-sm text-muted-foreground underline-offset-4 hover:text-primary hover:underline"
              >
                {t({ en: "All insights", ar: "جميع الأدلة" })}
              </LangLink>
              <span className="block text-sm font-semibold uppercase tracking-[0.2em] text-primary">
                {t(eyebrowLabel)}
              </span>
              <h1 className="mt-4 max-w-[23ch] text-3xl font-extrabold leading-tight text-foreground [text-wrap:balance] sm:text-4xl xl:text-5xl">
                {t(meta.title)}
              </h1>
              <p className="mt-5 text-lg leading-relaxed text-muted-foreground">
                {t(meta.description)}
              </p>
              <p className="mt-6 text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
                {t(updatedLabel)} {t(meta.dateLabel)}
              </p>
            </div>
            {meta.image && <EditorialImage image={meta.image} />}
          </div>
        </header>

        {/* ARTICLE BODY */}
        <div className="mx-auto grid max-w-[1280px] gap-12 px-5 py-12 text-start lg:grid-cols-[minmax(0,1fr)_280px] lg:gap-20 lg:px-8 lg:py-16">
          {meta.keyPoints || meta.sections ? (
            <div className="lg:order-2">
              <ArticleSidebar points={meta.keyPoints} sections={meta.sections} />
            </div>
          ) : (
            <aside className="order-2 border-t border-border pt-5 text-sm leading-relaxed lg:self-start">
              <h2 className="font-semibold text-foreground">
                {t({ en: "Explore the subject", ar: "للمزيد حول الموضوع" })}
              </h2>
              <LangLink
                to="/insights"
                className="mt-4 inline-flex min-h-11 items-center text-primary underline underline-offset-4"
              >
                {t({ en: "Browse IraqCert guides", ar: "تصفّح أدلة IraqCert" })}
              </LangLink>
            </aside>
          )}
          <article className="min-w-0 max-w-[75ch] lg:order-1 [&_h2]:scroll-mt-28 [&>p]:text-[17px] [&>p]:leading-[1.85]">
            {children}
          </article>
        </div>

        <section className="mx-auto max-w-[1280px] px-5 pb-12 lg:px-8">
          <LangLink to="/services" className="ic-text-link">
            {t({
              en: "Explore preparation and registration support",
              ar: "تعرّف على دعم التحضير والتسجيل",
            })}
          </LangLink>
        </section>
        {/* FAQ */}
        <FaqSection items={meta.faq} />

        {/* CTA */}
        <section className="bg-[#f5f3ef]">
          <div className="mx-auto max-w-3xl px-4 lg:px-8 py-20 text-center">
            <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">
              {t(
                meta.enquiryTitle ??
                  (meta.enquiryMessage
                    ? { en: "Discuss your ISO quotation", ar: "ناقش عرض السعر الذي تحتاجه" }
                    : content.services.cta.title),
              )}
            </h2>
            <a
              href={
                meta.enquiryMessage
                  ? `${whatsappUrl}?text=${encodeURIComponent(t(meta.enquiryMessage))}`
                  : whatsappUrl
              }
              target="_blank"
              rel="noopener noreferrer"
              className="group mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-8 py-3.5 text-base font-semibold text-primary-foreground shadow-soft transition-all hover:brightness-110"
            >
              {t(
                meta.enquiryButton ??
                  (meta.enquiryMessage
                    ? { en: "Request preparation support", ar: "اطلب دعم التحضير" }
                    : { en: "Discuss your requirement", ar: "ناقش متطلبك" }),
              )}{" "}
              {t({ en: "on WhatsApp", ar: "عبر واتساب" })}
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
